import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ExternalLink, Sparkles } from "lucide-react";
import {
  useActivity,
  useUpdateActivity,
  useDeleteActivity,
  useCompleteActivity,
  useGoals,
  usePlans,
} from "@/hooks/useCareer";
import {
  ProgressBar,
  StatusChip,
  fieldClassName,
} from "@/components/goals/goals-ui";
import { formatConfidence, isOpenWork } from "@/lib/goals-progress";

export function WorkDetailPage() {
  const { workId } = useParams<{ workId: string }>();
  const navigate = useNavigate();
  const activityQuery = useActivity(workId);
  const updateActivity = useUpdateActivity();
  const deleteActivity = useDeleteActivity();
  const completeActivity = useCompleteActivity();
  const goalsQuery = useGoals();
  const plansQuery = usePlans();

  const work = activityQuery.data?.activity;
  const goals = goalsQuery.data?.goals ?? [];
  const plans = plansQuery.data?.plans ?? [];
  const parentGoal = goals.find((g) => g.id === work?.goalId);
  const parentPlan = plans.find((p) => p.id === work?.planId);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [state, setState] = useState("planned");
  const [dueAt, setDueAt] = useState("");
  const [goalId, setGoalId] = useState("");
  const [planId, setPlanId] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [effortWeight, setEffortWeight] = useState("M");
  const [externalUrl, setExternalUrl] = useState("");
  const [confidence, setConfidence] = useState("");
  const [outcome, setOutcome] = useState("");

  useEffect(() => {
    if (!work) return;
    setTitle(work.title);
    setDescription(work.description ?? "");
    setState(work.state);
    setDueAt(
      work.dueAt
        ? new Date(work.dueAt).toISOString().slice(0, 16)
        : "",
    );
    setGoalId(work.goalId ?? "");
    setPlanId(work.planId ?? "");
    setProgressPercent(work.progressPercent ?? (work.state === "done" ? 100 : 0));
    setEffortWeight(work.effortWeight ?? "M");
    setExternalUrl(work.externalUrl ?? "");
    setConfidence(work.confidence ?? "");
    setOutcome(work.outcome ?? "");
  }, [work]);

  if (activityQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (!work) {
    return (
      <div>
        <Link to="/work" className="text-sm text-brand hover:underline">
          Back to Work
        </Link>
        <p className="mt-4 text-sm text-destructive">Work not found.</p>
      </div>
    );
  }

  async function save() {
    if (!workId) return;
    await updateActivity.mutateAsync({
      id: workId,
      input: {
        title: title.trim(),
        description: description.trim() || null,
        state,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        goalId: goalId || null,
        planId: planId || null,
        progressPercent: progressPercent as 0 | 50 | 100,
        effortWeight,
        externalUrl: externalUrl.trim() || null,
        confidence: confidence || null,
        outcome: outcome.trim() || null,
      },
    });
    setEditing(false);
  }

  async function markDone() {
    if (!workId) return;
    const note = window.prompt("Short outcome (optional)") ?? undefined;
    await completeActivity.mutateAsync({ id: workId, outcome: note || undefined });
  }

  async function handleDelete() {
    if (!workId || !confirm("Delete this work item?")) return;
    await deleteActivity.mutateAsync(workId);
    navigate("/work");
  }

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <Link to="/work" className="mb-4 inline-flex text-sm text-brand hover:underline">
        Back to Work
      </Link>

      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{work.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <StatusChip>{work.state}</StatusChip>
              <span>size {work.effortWeight ?? "M"}</span>
              {work.dueAt ? (
                <span>due {new Date(work.dueAt).toLocaleString()}</span>
              ) : null}
              {formatConfidence(work.confidence) ? (
                <span>{formatConfidence(work.confidence)} confidence</span>
              ) : null}
              {parentPlan ? (
                <Link to={`/plans/${parentPlan.id}`} className="text-brand hover:underline">
                  {parentPlan.title}
                </Link>
              ) : null}
              {parentGoal ? (
                <Link to={`/goals/${parentGoal.id}`} className="text-brand hover:underline">
                  {parentGoal.title}
                </Link>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/ai-assistant?workId=${encodeURIComponent(work.id)}`}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <Sparkles className="size-3.5" />
              Ask AI
            </Link>
            {isOpenWork(work.state) ? (
              <button
                type="button"
                onClick={() => void markDone()}
                className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground"
              >
                Mark done
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
            >
              {editing ? "Cancel" : "Edit"}
            </button>
          </div>
        </div>
      </header>

      {editing ? (
        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <input className={fieldClassName()} value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea
            className={fieldClassName()}
            rows={3}
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <select className={fieldClassName()} value={state} onChange={(e) => setState(e.target.value)}>
              <option value="planned">Planned</option>
              <option value="due">Due</option>
              <option value="done">Done</option>
              <option value="skipped">Skipped</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              className={fieldClassName()}
              value={String(progressPercent)}
              onChange={(e) => setProgressPercent(Number(e.target.value))}
            >
              <option value="0">Not started (0%)</option>
              <option value="50">Halfway (50%)</option>
              <option value="100">Done (100%)</option>
            </select>
            <select
              className={fieldClassName()}
              value={effortWeight}
              onChange={(e) => setEffortWeight(e.target.value)}
            >
              <option value="S">Size S</option>
              <option value="M">Size M</option>
              <option value="L">Size L</option>
            </select>
            <input
              type="datetime-local"
              className={fieldClassName()}
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <select className={fieldClassName()} value={goalId} onChange={(e) => setGoalId(e.target.value)}>
              <option value="">No linked goal</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
            <select className={fieldClassName()} value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">No linked plan</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
            <select
              className={fieldClassName()}
              value={confidence}
              onChange={(e) => setConfidence(e.target.value)}
            >
              <option value="">Confidence</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <input
            className={fieldClassName()}
            placeholder="External link (e.g. Jira)"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
          />
          <textarea
            className={fieldClassName()}
            rows={2}
            placeholder="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          />
          <div className="flex justify-between">
            <button type="button" onClick={() => void handleDelete()} className="text-xs text-destructive">
              Delete work
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={updateActivity.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </section>
      ) : (
        <>
          {work.description ? (
            <section className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Description
              </h3>
              <p className="mt-2 text-sm whitespace-pre-wrap">{work.description}</p>
            </section>
          ) : null}

          <section className="mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Progress
            </h3>
            <div className="mt-2 flex items-center gap-3">
              <ProgressBar
                value={work.progressPercent ?? (work.state === "done" ? 100 : 0)}
                className="flex-1"
              />
              <span className="text-sm tabular-nums">
                {work.progressPercent ?? (work.state === "done" ? 100 : 0)}%
              </span>
            </div>
          </section>

          {work.externalUrl ? (
            <a
              href={work.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline"
            >
              Open external link <ExternalLink className="size-3.5" />
            </a>
          ) : null}

          {work.outcome ? (
            <section className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Outcome
              </h3>
              <p className="mt-2 text-sm">{work.outcome}</p>
            </section>
          ) : null}

          {(work.jobId || work.companySlug) && (
            <section className="text-sm text-muted-foreground">
              {work.jobId ? (
                <p>
                  Job:{" "}
                  <Link to={`/jobs/${work.jobId}`} className="text-brand hover:underline">
                    {work.jobId}
                  </Link>
                </p>
              ) : null}
              {work.companySlug ? (
                <p>
                  Company:{" "}
                  <Link
                    to={`/companies/${work.companySlug}`}
                    className="text-brand hover:underline"
                  >
                    {work.companySlug}
                  </Link>
                </p>
              ) : null}
            </section>
          )}
        </>
      )}
    </div>
  );
}
